import { CSSProperties, useRef, useState, useCallback } from 'react';

/* ─────────────────────────────────────────────────
   LOGO 1 — "The Forge"
───────────────────────────────────────────────── */
const SPARKS: CSSProperties[] = [
  { left: '18%', animationDelay: '0s' },
  { left: '52%', animationDelay: '0.55s' },
  { left: '80%', animationDelay: '1.1s' },
];

function LogoForge({ light = false }: { light?: boolean }) {
  const textColor = light ? 'text-white' : 'text-stone-900';
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 rounded-xl shrink-0" style={{ overflow: 'visible' }}>
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-stone-700 to-stone-950" />
        <div className="absolute bottom-0 left-0 right-0 h-2 rounded-b-xl bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600 opacity-90" />
        <div className="absolute bottom-0 left-0 right-0 h-3/5 bg-gradient-to-t from-orange-500/60 via-amber-500/20 to-transparent rounded-b-xl" />
        <div className="absolute inset-0 flex items-center justify-center z-10 pb-1">
          <i className="ri-hammer-fill text-orange-300 text-xl" />
        </div>
        {SPARKS.map((style, i) => (
          <span key={i} className="adforge-spark" style={style} />
        ))}
      </div>
      <span className={`font-black tracking-tight leading-none select-none text-3xl ${textColor}`}>
        Ad<span className="text-orange-500">Forge</span>
        <span className={`ml-1.5 text-[0.4em] font-extrabold tracking-widest align-top relative top-[0.2em] ${light ? 'text-orange-300' : 'text-orange-500'}`}>
          AI
        </span>
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 2 — "Ember"
───────────────────────────────────────────────── */
function LogoEmber({ light = false }: { light?: boolean }) {
  const nameColor = light ? 'text-white' : 'text-stone-900';
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
        <div
          className="absolute inset-0 rounded-full bg-orange-400/60"
          style={{ animation: 'adforge-pulse-ring 2s ease-out infinite' }}
        />
        <div className="relative w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #f97316 0%, #d97706 100%)' }}>
          <i
            className="ri-fire-fill text-white text-2xl z-10"
            style={{ animation: 'adforge-flame-flicker 2.5s ease-in-out infinite' }}
          />
        </div>
      </div>
      <div className="flex flex-col leading-none gap-0.5">
        <div className="flex items-baseline gap-0">
          <span className={`font-light text-2xl tracking-tight ${nameColor}`}>Ad</span>
          <span className={`font-black text-2xl tracking-tight ${nameColor}`}>FORGE</span>
        </div>
        <span className="text-[10px] font-extrabold tracking-[0.35em] text-orange-500 uppercase">
          Artificial Intelligence
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 3 — "Strike"
   Fixed: "Ad" stays mixed-case (not all-caps)
───────────────────────────────────────────────── */
function LogoStrike({ light = false }: { light?: boolean }) {
  const chipText = light ? 'text-orange-300 border-orange-400' : 'text-orange-600 border-orange-500';
  const subText = light ? 'text-stone-400' : 'text-stone-400';
  const wordmarkColor = light ? 'text-white' : 'text-stone-900';
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 rounded-xl bg-stone-900 flex items-center justify-center overflow-hidden shrink-0">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-orange-400 via-orange-500 to-amber-600" />
        <i className="ri-flashlight-fill text-orange-400 text-2xl ml-1" />
        <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-orange-500/20 to-transparent" />
      </div>
      <div className="flex flex-col leading-none gap-1">
        <div className="flex items-center gap-2">
          <span className={`font-black text-2xl tracking-[0.06em] ${wordmarkColor} ${!light ? 'adforge-shimmer-text' : ''}`}>
            Ad<span className="text-orange-500">Forge</span>
          </span>
          <span className={`text-[10px] font-bold tracking-widest border rounded px-1.5 py-0.5 ${chipText}`}>
            AI
          </span>
        </div>
        <span className={`text-[10px] tracking-[0.2em] font-medium uppercase ${subText}`}>
          Ad Creation Platform
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 4 — "Orbit"
   Orbiting spark dots around center hammer badge
   Thin "Ad" + black "Forge" + bordered "AI" chip
───────────────────────────────────────────────── */
function LogoOrbit({ light = false }: { light?: boolean }) {
  const textColor = light ? 'text-white' : 'text-stone-900';
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 flex items-center justify-center shrink-0">
        {/* Dashed orbit ring */}
        <div className="absolute inset-0 rounded-full border border-dashed border-orange-400/50" />
        {/* Primary orbiting dot — clockwise */}
        <div
          className="absolute inset-0"
          style={{ animation: 'adforge-orbit-spin 5s linear infinite' }}
        >
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-orange-400"
            style={{ filter: 'drop-shadow(0 0 4px #f97316)' }}
          />
        </div>
        {/* Secondary orbiting dot — counter-clockwise, slower */}
        <div
          className="absolute inset-0"
          style={{ animation: 'adforge-orbit-spin 8s linear infinite reverse' }}
        >
          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-300/70" />
        </div>
        {/* Center badge */}
        <div className="w-8 h-8 rounded-full bg-stone-900 flex items-center justify-center z-10">
          <i
            className="ri-hammer-fill text-orange-400 text-sm"
            style={{ animation: 'adforge-neon-pulse 3s ease-in-out infinite' }}
          />
        </div>
        {/* Subtle bottom glow */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-6 h-2 bg-orange-500/30 blur-sm rounded-full z-0" />
      </div>
      {/* Wordmark */}
      <div className="flex flex-col leading-none gap-1 select-none">
        <div className="flex items-center gap-1.5">
          <span className={`text-3xl font-thin tracking-tight ${textColor}`}>Ad</span>
          <span className="text-3xl font-black text-orange-500 tracking-tight">Forge</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="h-px flex-1 bg-gradient-to-r from-orange-500 to-transparent"
            style={{ animation: 'adforge-slide-glow 2.5s ease-in-out infinite' }}
          />
          <span className={`text-[9px] font-bold tracking-[0.25em] uppercase ${light ? 'text-orange-300' : 'text-orange-500'}`}>AI</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 5 — "Neon Edge"
   Glowing neon-border badge + fire sparks
   Bold merged "AdForge" + animated underline bar
───────────────────────────────────────────────── */
const NEON_SPARKS: CSSProperties[] = [
  { left: '20%', animationDelay: '0.2s' },
  { left: '55%', animationDelay: '0.9s' },
  { left: '78%', animationDelay: '1.6s' },
];

function LogoNeon({ light = false }: { light?: boolean }) {
  const textColor = light ? 'text-white' : 'text-stone-900';
  return (
    <div className="flex items-center gap-3">
      {/* Neon-border badge */}
      <div
        className="relative w-12 h-12 rounded-xl bg-stone-950 flex items-center justify-center shrink-0"
        style={{
          border: '1.5px solid #f97316',
          animation: 'adforge-neon-pulse 2.2s ease-in-out infinite',
          overflow: 'visible',
        }}
      >
        <i className="ri-hammer-fill text-orange-400 text-xl z-10" />
        {/* Corner accent dots */}
        <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-orange-500" />
        <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-orange-400/60" />
        {/* Inner glow */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-orange-500/20 to-transparent" />
        {/* Sparks */}
        {NEON_SPARKS.map((style, i) => (
          <span key={i} className="adforge-spark" style={{ ...style, bottom: '10px' }} />
        ))}
      </div>
      {/* Wordmark */}
      <div className="flex flex-col leading-none gap-1.5 select-none">
        <span className={`text-3xl font-black tracking-tight ${textColor}`}>
          Ad<span className="text-orange-500">Forge</span>
          <span className={`ml-1.5 text-[0.38em] font-extrabold tracking-widest align-super ${light ? 'text-orange-300' : 'text-orange-500'}`}>
            AI
          </span>
        </span>
        {/* Animated sliding underline */}
        <div className="relative h-[2px] w-full overflow-hidden rounded-full bg-stone-200/20">
          <div
            className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-600 via-amber-400 to-orange-600"
            style={{ animation: 'adforge-slide-glow 1.8s ease-in-out infinite', transformOrigin: 'left center' }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 6 — "Anvil"
   CSS-crafted anvil shape + rising sparks + orange forge glow
───────────────────────────────────────────────── */
const ANVIL_SPARKS: CSSProperties[] = [
  { left: '15%', animationDelay: '0s' },
  { left: '48%', animationDelay: '0.7s' },
  { left: '82%', animationDelay: '1.4s' },
];

function AnvilShape({ glowColor = '#f97316' }: { glowColor?: string }) {
  return (
    <div className="relative flex flex-col items-center" style={{ width: 32, height: 26 }}>
      {/* Horn (left protrusion of anvil top) */}
      <div
        className="absolute"
        style={{
          top: 0,
          left: 0,
          width: 9,
          height: 7,
          background: glowColor,
          borderRadius: '2px 0 0 2px',
          opacity: 0.85,
        }}
      />
      {/* Main top face */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 7,
          right: 0,
          height: 7,
          background: glowColor,
          borderRadius: '2px 4px 0 0',
        }}
      />
      {/* Waist */}
      <div
        style={{
          position: 'absolute',
          top: 7,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 14,
          height: 6,
          background: glowColor,
          opacity: 0.75,
        }}
      />
      {/* Base */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 8,
          background: glowColor,
          borderRadius: '2px',
          opacity: 0.9,
        }}
      />
    </div>
  );
}

function LogoAnvil({ light = false }: { light?: boolean }) {
  const textColor = light ? 'text-white' : 'text-stone-900';
  return (
    <div className="flex items-center gap-3">
      {/* Badge */}
      <div
        className="relative w-12 h-12 rounded-xl bg-stone-950 flex items-center justify-center shrink-0"
        style={{ overflow: 'visible' }}
      >
        {/* Pulsing neon bottom glow */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-3 rounded-full blur-md z-0"
          style={{
            background: '#f97316',
            opacity: 0.5,
            animation: 'adforge-neon-pulse 2s ease-in-out infinite',
          }}
        />
        {/* Inner fire bloom */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-orange-600/40 via-amber-500/10 to-transparent" />
        {/* Anvil */}
        <div className="relative z-10">
          <AnvilShape glowColor="#ffa040" />
        </div>
        {/* Sparks */}
        {ANVIL_SPARKS.map((style, i) => (
          <span key={i} className="adforge-spark" style={style} />
        ))}
        {/* Top highlight strip */}
        <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl bg-gradient-to-r from-transparent via-orange-400/60 to-transparent" />
      </div>

      {/* Wordmark */}
      <div className="flex flex-col leading-none gap-1 select-none">
        <div className="flex items-baseline gap-0.5">
          <span className={`text-3xl font-thin tracking-tight ${textColor}`}>Ad</span>
          <span className="text-3xl font-black text-orange-500 tracking-tight">Forge</span>
          <span
            className={`ml-1.5 text-[0.38em] font-extrabold tracking-widest align-super ${light ? 'text-orange-300' : 'text-orange-500'}`}
          >
            AI
          </span>
        </div>
        {/* Ember underline — animated heat shimmer */}
        <div className="relative h-[2px] w-full rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-stone-200/30 rounded-full" />
          <div
            className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-600 via-amber-300 to-orange-600"
            style={{ animation: 'adforge-slide-glow 2.2s ease-in-out infinite' }}
          />
        </div>
        <span className={`text-[9px] tracking-[0.22em] font-semibold uppercase ${light ? 'text-stone-400' : 'text-stone-400'}`}>
          Forged by AI
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 7 — "Dragonstone"
   Ice & Fire split badge · Crown · Glowing Sword
   Serif "Ad" (ice-blue) + "Forge" (fire-orange) · Born of Ice & Fire
───────────────────────────────────────────────── */
const DRAGON_SPARKS_ICE: CSSProperties[] = [
  { left: '6%', animationDelay: '0s' },
  { left: '24%', animationDelay: '1.1s' },
];
const DRAGON_SPARKS_FIRE: CSSProperties[] = [
  { left: '63%', animationDelay: '0.5s' },
  { left: '84%', animationDelay: '1.4s' },
];

function LogoDragonstone({ light = false }: { light?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {/* Badge */}
      <div className="relative w-12 h-12 shrink-0" style={{ overflow: 'visible' }}>
        {/* Obsidian base */}
        <div className="absolute inset-0 rounded-xl" style={{ background: '#07070f' }} />

        {/* Ice half (left) */}
        <div
          className="absolute top-0 left-0 w-1/2 h-full rounded-l-xl"
          style={{ background: 'linear-gradient(135deg, rgba(147,210,250,0.18) 0%, transparent 80%)' }}
        />
        {/* Fire half (right) */}
        <div
          className="absolute top-0 right-0 w-1/2 h-full rounded-r-xl"
          style={{ background: 'linear-gradient(225deg, rgba(249,115,22,0.22) 0%, transparent 80%)' }}
        />

        {/* Vertical sword-blade divider */}
        <div
          className="absolute top-1.5 bottom-1.5 left-1/2 w-px -translate-x-1/2 z-10"
          style={{
            background:
              'linear-gradient(to bottom, rgba(147,210,250,0.9) 0%, rgba(220,220,220,0.7) 45%, rgba(249,115,22,0.9) 100%)',
          }}
        />

        {/* Sword icon */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <i
            className="ri-sword-fill text-stone-200 text-xl"
            style={{
              filter:
                'drop-shadow(0 -2px 4px rgba(147,210,250,0.7)) drop-shadow(0 2px 5px rgba(249,115,22,0.6))',
              transform: 'rotate(180deg)',
            }}
          />
        </div>

        {/* Crown floating above (overflow visible) */}
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-30">
          <i
            className="ri-crown-fill text-amber-400 text-sm"
            style={{
              filter: 'drop-shadow(0 0 5px #fbbf24) drop-shadow(0 0 10px #f59e0b)',
              animation: 'adforge-neon-pulse 3s ease-in-out infinite',
            }}
          />
        </div>

        {/* Ice sparks (left side — blue override) */}
        {DRAGON_SPARKS_ICE.map((s, i) => (
          <span
            key={i}
            className="adforge-spark"
            style={{ ...s, background: '#93d2fa', boxShadow: '0 0 5px #93d2fa' }}
          />
        ))}
        {/* Fire sparks (right side — orange) */}
        {DRAGON_SPARKS_FIRE.map((s, i) => (
          <span key={i} className="adforge-spark" style={s} />
        ))}

        {/* Bottom split glow */}
        <div
          className="absolute bottom-0 left-0 w-1/2 h-2 blur-sm rounded-bl-xl"
          style={{ background: 'rgba(147,210,250,0.45)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-1/2 h-2 blur-sm rounded-br-xl"
          style={{ background: 'rgba(249,115,22,0.5)' }}
        />

        {/* Outer border */}
        <div
          className="absolute inset-0 rounded-xl"
          style={{ border: '1px solid rgba(200,200,220,0.18)' }}
        />
      </div>

      {/* Wordmark */}
      <div className="flex flex-col leading-none gap-1 select-none">
        <div className="flex items-baseline gap-0">
          <span
            className="text-2xl font-black tracking-tight"
            style={{
              color: light ? '#93d2fa' : '#4fa8d0',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            Ad
          </span>
          <span
            className="text-2xl font-black tracking-tight"
            style={{
              color: '#d95500',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            Forge
          </span>
          <span className="ml-1.5 text-[0.36em] font-extrabold tracking-widest align-super text-amber-400">
            AI
          </span>
        </div>

        {/* Dual-tone underline bar */}
        <div className="flex h-[2px] w-full rounded-full overflow-hidden">
          <div
            className="flex-1"
            style={{
              background: 'linear-gradient(to right, #93d2fa, #5bafd4)',
              animation: 'adforge-slide-glow 2.2s ease-in-out infinite',
            }}
          />
          <div
            className="flex-1"
            style={{
              background: 'linear-gradient(to right, #d95500, #f97316)',
              animation: 'adforge-slide-glow 2.2s ease-in-out infinite reverse',
            }}
          />
        </div>

        <span className="text-[9px] tracking-[0.22em] font-semibold uppercase text-stone-400">
          Born of Ice &amp; Fire
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 8 — "Fire & Blood"
   Targaryen coin badge · Crimson/Obsidian split
   Gold seam + gold crown · Flame center
   Gold "Ad" + Crimson "Forge" · "Fire and Blood"
───────────────────────────────────────────────── */
const FIRE_BLOOD_SPARKS: CSSProperties[] = [
  { left: '12%', animationDelay: '0.1s' },
  { left: '48%', animationDelay: '0.8s' },
  { left: '82%', animationDelay: '1.6s' },
];

function LogoFireAndBlood({ light = false }: { light?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {/* Round coin/sigil badge */}
      <div className="relative w-12 h-12 shrink-0 rounded-full" style={{ overflow: 'visible' }}>
        {/* Left half — Targaryen crimson */}
        <div
          className="absolute top-0 left-0 w-1/2 h-full rounded-l-full"
          style={{ background: 'linear-gradient(150deg, #7a0000 0%, #4d0000 100%)' }}
        />
        {/* Right half — obsidian */}
        <div
          className="absolute top-0 right-0 w-1/2 h-full rounded-r-full"
          style={{ background: 'linear-gradient(210deg, #1a0404 0%, #040000 100%)' }}
        />
        {/* Gold seam divider */}
        <div
          className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 z-10"
          style={{
            background:
              'linear-gradient(to bottom, transparent 5%, #c8a84b 20%, #e2c97a 50%, #c8a84b 80%, transparent 95%)',
          }}
        />
        {/* Gold outer ring border */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            border: '1.5px solid rgba(200,168,75,0.65)',
            animation: 'adforge-neon-pulse 3.5s ease-in-out infinite',
          }}
        />
        {/* Central flame */}
        <div className="absolute inset-0 flex items-center justify-center z-20 pb-1">
          <i
            className="ri-fire-fill text-xl"
            style={{
              color: '#d4a84b',
              filter: 'drop-shadow(0 0 5px rgba(212,168,75,0.85))',
              animation: 'adforge-flame-flicker 2s ease-in-out infinite',
            }}
          />
        </div>
        {/* Bottom crimson glow pool */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-2 blur-sm z-0 rounded-full"
          style={{
            background: 'rgba(180,10,10,0.7)',
            animation: 'adforge-neon-pulse 2.5s ease-in-out infinite',
          }}
        />
        {/* Crown above */}
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-30">
          <i
            className="ri-crown-fill text-sm"
            style={{
              color: '#c8a84b',
              filter: 'drop-shadow(0 0 5px rgba(200,168,75,0.95)) drop-shadow(0 0 10px rgba(200,168,75,0.5))',
              animation: 'adforge-neon-pulse 3s ease-in-out infinite',
            }}
          />
        </div>
        {/* Crimson sparks */}
        {FIRE_BLOOD_SPARKS.map((s, i) => (
          <span
            key={i}
            className="adforge-spark"
            style={{ ...s, background: '#dc2626', boxShadow: '0 0 4px #dc2626' }}
          />
        ))}
      </div>

      {/* Wordmark */}
      <div className="flex flex-col leading-none gap-1 select-none">
        <div className="flex items-baseline gap-0">
          <span
            className="text-2xl font-black tracking-tight"
            style={{ color: '#c8a84b', fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            Ad
          </span>
          <span
            className="text-2xl font-black tracking-tight"
            style={{
              color: light ? '#f87171' : '#b91c1c',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            Forge
          </span>
          <span
            className="ml-1.5 text-[0.36em] font-extrabold tracking-widest align-super"
            style={{ color: '#c8a84b' }}
          >
            AI
          </span>
        </div>
        {/* Crimson-to-gold underline */}
        <div className="relative h-[2px] w-full rounded-full overflow-hidden">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'linear-gradient(to right, #b91c1c, #c8a84b, #b91c1c)',
              animation: 'adforge-slide-glow 2.5s ease-in-out infinite',
            }}
          />
        </div>
        <span className="text-[9px] tracking-[0.22em] font-semibold uppercase text-stone-400">
          Fire and Blood
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   LOGO 9 — "The Long Night"
   Void-black badge · Six ice-crystal shard rays
   Glowing frost core · One last ember
   Pale silver "Ad" + frost-blue "Forge" · "Winter Came. We Endured."
───────────────────────────────────────────────── */
const LONG_NIGHT_RAY_ANGLES = [0, 30, 60, 90, 120, 150];

function LogoLongNight({ light = false }: { light?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 shrink-0" style={{ overflow: 'visible' }}>
        {/* Void-black base */}
        <div className="absolute inset-0 rounded-xl" style={{ background: '#020408' }} />
        {/* Cold radial glow from center */}
        <div
          className="absolute inset-0 rounded-xl"
          style={{
            background:
              'radial-gradient(ellipse at 50% 45%, rgba(147,210,250,0.15) 0%, transparent 65%)',
          }}
        />

        {/* Six ice-shard rays — snowflake crystal */}
        {LONG_NIGHT_RAY_ANGLES.map((deg, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              top: '50%',
              left: '50%',
              width: '30px',
              height: '1px',
              transform: `translate(-50%, -50%) rotate(${deg}deg)`,
              background:
                'linear-gradient(to right, transparent 0%, rgba(147,210,250,0.5) 25%, rgba(220,242,252,0.85) 50%, rgba(147,210,250,0.5) 75%, transparent 100%)',
              zIndex: 5,
            }}
          />
        ))}
        {/* Ray tip dots — secondary crystal nubs */}
        {LONG_NIGHT_RAY_ANGLES.map((deg, i) => {
          const rad = (deg * Math.PI) / 180;
          const r = 13;
          const dx = Math.cos(rad) * r;
          const dy = Math.sin(rad) * r;
          return (
            <>
              <div
                key={`tip-a-${i}`}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  top: `calc(50% + ${dy}px)`,
                  left: `calc(50% + ${dx}px)`,
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(200,235,252,0.7)',
                  zIndex: 6,
                }}
              />
              <div
                key={`tip-b-${i}`}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  top: `calc(50% + ${-dy}px)`,
                  left: `calc(50% + ${-dx}px)`,
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(200,235,252,0.7)',
                  zIndex: 6,
                }}
              />
            </>
          );
        })}

        {/* Central frost orb */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div
            className="w-4 h-4 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(240,250,255,0.98) 0%, rgba(147,210,250,0.8) 55%, rgba(80,160,210,0.2) 100%)',
              filter:
                'drop-shadow(0 0 6px rgba(147,210,250,1)) drop-shadow(0 0 14px rgba(147,210,250,0.6))',
              animation: 'adforge-neon-pulse 4s ease-in-out infinite',
            }}
          />
        </div>

        {/* Single last ember — the fire that survived */}
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-20">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: '#f97316',
              boxShadow: '0 0 5px #f97316, 0 0 10px rgba(249,115,22,0.45)',
              animation: 'adforge-neon-pulse 1.8s ease-in-out infinite',
            }}
          />
        </div>

        {/* Frost border */}
        <div
          className="absolute inset-0 rounded-xl"
          style={{ border: '1px solid rgba(147,210,250,0.22)' }}
        />
      </div>

      {/* Wordmark */}
      <div className="flex flex-col leading-none gap-1 select-none">
        <div className="flex items-baseline gap-0">
          <span
            className="text-2xl font-black tracking-tight"
            style={{
              color: light ? '#daeef8' : '#9bbccc',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            Ad
          </span>
          <span
            className="text-2xl font-black tracking-tight"
            style={{
              color: '#6aaec8',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            Forge
          </span>
          <span
            className="ml-1.5 text-[0.36em] font-extrabold tracking-widest align-super"
            style={{ color: '#f97316' }}
          >
            AI
          </span>
        </div>
        {/* Frost underline */}
        <div className="relative h-[2px] w-full rounded-full overflow-hidden">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                'linear-gradient(to right, rgba(147,210,250,0.3), rgba(220,242,252,0.9), rgba(147,210,250,0.3))',
              animation: 'adforge-slide-glow 3.5s ease-in-out infinite',
            }}
          />
        </div>
        <span className="text-[9px] tracking-[0.22em] font-semibold uppercase text-stone-400">
          Winter Came. We Endured.
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   Download helper — renders a logo node to PNG via Canvas
   Uses foreignObject SVG approach with isolated stylesheet
   to avoid cross-origin CSS access errors from html-to-image
───────────────────────────────────────────────── */
async function downloadLogoAsPng(node: HTMLElement, filename: string, darkBg: boolean) {
  const PAD = 32;
  const SCALE = 3;

  const rect = node.getBoundingClientRect();
  const w = Math.ceil(rect.width) + PAD * 2;
  const h = Math.ceil(rect.height) + PAD * 2;

  // Clone the node so we can inline all computed styles safely
  const clone = node.cloneNode(true) as HTMLElement;

  // Inline computed styles on every element to avoid needing external stylesheets
  const inlineStyles = (src: Element, dst: Element) => {
    const computed = window.getComputedStyle(src);
    const el = dst as HTMLElement;
    // Copy all computed style properties
    Array.from(computed).forEach((prop) => {
      try {
        el.style.setProperty(prop, computed.getPropertyValue(prop));
      } catch {
        // ignore read-only or unsupported props
      }
    });
    for (let i = 0; i < src.children.length; i++) {
      inlineStyles(src.children[i], dst.children[i]);
    }
  };
  inlineStyles(node, clone);

  // Wrap in a container with background + padding
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${PAD}px;
    background: ${darkBg ? '#1c1917' : 'transparent'};
    width: ${w}px;
    height: ${h}px;
    box-sizing: border-box;
  `;
  wrapper.appendChild(clone);

  // Serialize to SVG foreignObject
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('xmlns', svgNS);
  svg.setAttribute('width', String(w));
  svg.setAttribute('height', String(h));

  const fo = document.createElementNS(svgNS, 'foreignObject');
  fo.setAttribute('x', '0');
  fo.setAttribute('y', '0');
  fo.setAttribute('width', String(w));
  fo.setAttribute('height', String(h));
  fo.appendChild(wrapper);
  svg.appendChild(fo);

  const svgStr = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * SCALE;
      canvas.height = h * SCALE;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(SCALE, SCALE);
      if (darkBg) {
        ctx.fillStyle = '#1c1917';
        ctx.fillRect(0, 0, w, h);
      }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(svgUrl);

      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        resolve();
      }, 'image/png');
    };
    img.onerror = reject;
    img.src = svgUrl;
  });
}

/* ─────────────────────────────────────────────────
   Preview Card
───────────────────────────────────────────────── */
interface PreviewCardProps {
  number: number;
  name: string;
  tagline: string;
  traits: string[];
  logo: React.ReactNode;
  logoDark: React.ReactNode;
}

function PreviewCard({ number, name, tagline, traits, logo, logoDark }: PreviewCardProps) {
  const isNew = number >= 8;
  const lightRef = useRef<HTMLDivElement>(null);
  const darkRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState<'light' | 'dark' | null>(null);

  const handleDownload = useCallback(async (variant: 'light' | 'dark') => {
    const node = variant === 'light' ? lightRef.current : darkRef.current;
    if (!node) return;
    setDownloading(variant);
    try {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      await downloadLogoAsPng(
        node,
        `adforge-logo-${slug}-${variant}.png`,
        variant === 'dark',
      );
    } catch (e) {
      console.error('Logo download failed', e);
    } finally {
      setDownloading(null);
    }
  }, [name]);

  return (
    <div className={`rounded-2xl border overflow-hidden ${isNew ? 'border-orange-300 ring-2 ring-orange-400/30' : 'border-stone-200'}`}>
      <div className={`px-6 py-4 border-b ${isNew ? 'bg-orange-50 border-orange-200' : 'bg-stone-50 border-stone-200'}`}>
        <div className="flex items-center gap-3 mb-1">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-black flex items-center justify-center shrink-0">
            {number}
          </span>
          <h2 className="text-lg font-black text-stone-900">{name}</h2>
          {isNew && (
            <span className="text-[10px] font-bold text-orange-600 bg-orange-100 border border-orange-300 rounded-full px-2 py-0.5 tracking-wide uppercase">
              New
            </span>
          )}
        </div>
        <p className="text-sm text-stone-500 ml-10">{tagline}</p>
      </div>

      {/* Light variant */}
      <div className="relative px-8 py-10 bg-white flex items-center justify-center border-b border-stone-100 group">
        <div ref={lightRef} className="inline-flex items-center">
          {logo}
        </div>
        <button
          onClick={() => handleDownload('light')}
          disabled={downloading !== null}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-orange-500 hover:text-white text-stone-500 text-xs font-semibold transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100 whitespace-nowrap disabled:opacity-50"
        >
          {downloading === 'light' ? (
            <><i className="ri-loader-4-line animate-spin" /> Exporting…</>
          ) : (
            <><i className="ri-download-2-line" /> PNG (transparent)</>
          )}
        </button>
      </div>

      {/* Dark variant */}
      <div className="relative px-8 py-10 bg-stone-900 flex items-center justify-center border-b border-stone-700 group">
        <div ref={darkRef} className="inline-flex items-center">
          {logoDark}
        </div>
        <button
          onClick={() => handleDownload('dark')}
          disabled={downloading !== null}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-700 hover:bg-orange-500 hover:text-white text-stone-400 text-xs font-semibold transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100 whitespace-nowrap disabled:opacity-50"
        >
          {downloading === 'dark' ? (
            <><i className="ri-loader-4-line animate-spin" /> Exporting…</>
          ) : (
            <><i className="ri-download-2-line" /> PNG (dark bg)</>
          )}
        </button>
      </div>

      <div className="px-6 py-4 bg-stone-50">
        <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-2">Design Style</p>
        <div className="flex flex-wrap gap-2">
          {traits.map(t => (
            <span key={t} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-3 py-1 font-medium">
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   Page
───────────────────────────────────────────────── */
export default function LogoPreviewPage() {
  const logos = [
    {
      number: 1,
      name: 'The Forge',
      tagline: 'Hammer + fire sparks — bold and energetic',
      traits: ['Animated Sparks', 'Fire Glow', 'Dark Badge', 'Warm & Bold'],
      logo: <LogoForge />,
      logoDark: <LogoForge light />,
    },
    {
      number: 2,
      name: 'Ember',
      tagline: 'Flame circle + soft glow pulse — modern and sleek',
      traits: ['Glowing Circle', 'Flame Flicker', 'Thin + Bold Wordmark', 'Clean & Premium'],
      logo: <LogoEmber />,
      logoDark: <LogoEmber light />,
    },
    {
      number: 3,
      name: 'Strike',
      tagline: 'Industrial badge + shimmer text — bold and distinctive',
      traits: ['Orange Accent Stripe', 'Lightning Bolt', 'Shimmer Animation', 'Industrial Edge'],
      logo: <LogoStrike />,
      logoDark: <LogoStrike light />,
    },
    {
      number: 4,
      name: 'Orbit',
      tagline: 'Orbiting sparks around hammer — dynamic & techy',
      traits: ['Dual Orbit Dots', 'Neon Glow', 'Thin + Black Wordmark', 'Animated Line'],
      logo: <LogoOrbit />,
      logoDark: <LogoOrbit light />,
    },
    {
      number: 5,
      name: 'Neon Edge',
      tagline: 'Glowing neon border badge + animated underline',
      traits: ['Neon Border Pulse', 'Animated Sparks', 'Corner Dots', 'Sliding Underline'],
      logo: <LogoNeon />,
      logoDark: <LogoNeon light />,
    },
    {
      number: 6,
      name: 'Anvil',
      tagline: 'CSS-crafted anvil + rising sparks + forge glow — the OG symbol',
      traits: ['Real Anvil Shape', 'Animated Sparks', 'Forge Heat Glow', 'Shimmer Underline'],
      logo: <LogoAnvil />,
      logoDark: <LogoAnvil light />,
    },
    {
      number: 7,
      name: 'Dragonstone',
      tagline: 'Ice & Fire split — glowing sword, crown, born of two forces',
      traits: ['Ice/Fire Split Badge', 'Glowing Crown', 'Dual Sparks', 'Serif Wordmark', 'Born of Ice & Fire'],
      logo: <LogoDragonstone />,
      logoDark: <LogoDragonstone light />,
    },
    {
      number: 8,
      name: 'Fire & Blood',
      tagline: 'Targaryen coin badge — crimson, obsidian, gold seam, crowned flame',
      traits: ['Round Coin Badge', 'Crimson/Obsidian Split', 'Gold Crown', 'Targaryen Colors', 'Fire and Blood'],
      logo: <LogoFireAndBlood />,
      logoDark: <LogoFireAndBlood light />,
    },
    {
      number: 9,
      name: 'The Long Night',
      tagline: 'Void-black ice crystal — six frost rays, glowing core, last ember',
      traits: ['Six Ice-Shard Rays', 'Frost Crystal Core', 'Last Ember Dot', 'Pure Ice Palette', 'Winter Came. We Endured.'],
      logo: <LogoLongNight />,
      logoDark: <LogoLongNight light />,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="border-b border-stone-200 bg-stone-50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-stone-900">Logo Preview</h1>
            <p className="text-sm text-stone-500 mt-0.5">9 designs — Options 8 &amp; 9 are the newest. Tell me which you want and I&apos;ll roll it out site-wide.</p>
          </div>
          <span className="text-xs text-stone-400 border border-stone-200 rounded-full px-3 py-1.5 bg-white whitespace-nowrap">
            Internal preview only
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Intro note */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-4 mb-10 flex gap-3 items-start">
          <i className="ri-information-line text-orange-500 text-lg mt-0.5 shrink-0" />
          <p className="text-sm text-orange-800">
            Each design shows on a <strong>white background</strong> (navbar / public pages) and a <strong>dark background</strong> (sidebar / footer).
            New: <strong>Option 8 — Fire &amp; Blood</strong> (Targaryen crimson + gold) and <strong>Option 9 — The Long Night</strong> (ice crystal frost + one last ember).
          </p>
        </div>

        {/* Logo grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {logos.map(l => (
            <PreviewCard key={l.number} {...l} />
          ))}
        </div>

        {/* CTA footer */}
        <div className="mt-12 text-center bg-stone-900 rounded-2xl py-10 px-8">
          <p className="text-white font-black text-xl mb-2">Which one do you want?</p>
          <p className="text-stone-400 text-sm">
            Reply with{' '}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n, i, arr) => (
              <span key={n}>
                <span className="text-orange-400 font-bold">Option {n}</span>
                {i < arr.length - 2 ? ', ' : i === arr.length - 2 ? ', or ' : ''}
              </span>
            ))}{' '}
            — and I&apos;ll apply it across the entire site instantly.
          </p>
        </div>
      </div>
    </div>
  );
}
